import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Grid as GridLegacy,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Pie } from 'react-chartjs-2';
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek, parse, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';
import '../utils/chartSetup';

// TypeScript Interfaces
interface UserMapping {
  username: string;
  team?: string;
  sales_tracker_id?: string;
}

interface SalesRecord {
  [key: string]: any;
  '4'?: string;
  Date?: string;
  'Agent '?: string;
  Agent?: string;
}

interface SalesInfo {
  data?: SalesRecord[];
  totalRecords?: number;
  headers?: string[];
}

interface AgentData {
  name: string;
  count: number;
  team: string | null;
}

interface PieChartData {
  name: string;
  value: number;
  color: string;
}

// Constants
const COLORS = ['#ff9800', '#f57c00', '#e65100', '#ffb300', '#ffa000', '#ff8f00', '#ff6f00', '#ffe082'];
const DAILY_GOAL = 45;
const TEAM_NAME = 'Weekend Warriors';

const PartnerAnalytics: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [allUsers, setAllUsers] = useState<UserMapping[]>([]);

  // Fetch all users to map agent names to teams
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010'}/api/user-management/users/team-mapping`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const data = await response.json();
        if (data.success) {
          setAllUsers(data.users);
        }
      } catch (error) {
        console.error('Error fetching users for team mapping:', error);
      }
    };
    fetchUsers();
  }, []);

  const {
    data: salesData,
    isLoading: salesLoading,
    error: salesError,
  } = useQuery<{ data: SalesInfo }>({
    queryKey: ['partnerAnalyticsSalesData'],
    queryFn: () => api.getSalesData(),
    refetchInterval: 5 * 60 * 1000,
  });

  const salesInfo: SalesInfo = salesData?.data || {};
  const rawData: SalesRecord[] = salesInfo.data || [];

  const parseSheetDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    try {
      let parsedDate: Date | null = null;
      const dashMatch = dateString.match(/(\d+)-([A-Za-z]+)-(\d+)/);
      if (dashMatch) {
        const day = parseInt(dashMatch[1], 10);
        const monthStr = dashMatch[2];
        let year = parseInt(dashMatch[3], 10);
        if (year < 100) year += 2000;
        const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const monthIndex = monthNames.indexOf(monthStr.toLowerCase());
        if (monthIndex !== -1) parsedDate = new Date(year, monthIndex, day);
      } else if (dateString.includes('/')) {
        const slashMatch = dateString.match(/(\d+)\/(\d+)\/(\d+)/);
        if (slashMatch) {
          parsedDate = new Date(parseInt(slashMatch[3], 10), parseInt(slashMatch[2], 10) - 1, parseInt(slashMatch[1], 10));
        }
      } else if (dateString.includes(',')) {
        let corrected = dateString.includes('2027') ? dateString.replace('2027', '2025') : dateString;
        parsedDate = parse(corrected, 'MMM dd, yyyy', new Date());
      } else {
        parsedDate = new Date(dateString);
      }
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const currentYear = new Date().getFullYear();
        if (parsedDate.getFullYear() > currentYear + 1) return null;
        return parsedDate;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const normalizeDate = (date: Date | string | null): string | null => {
    if (!date) return null;
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const filteredData = useMemo(() => {
    if (dateFilter === 'all') return rawData;
    const now = new Date();
    return rawData.filter(record => {
      const dateValue = record['4'] || record.Date;
      if (!dateValue) return false;
      const recordDate = parseSheetDate(dateValue);
      if (!recordDate || isNaN(recordDate.getTime())) return false;
      const normalizedRecordDate = normalizeDate(recordDate);
      switch (dateFilter) {
        case 'today': return normalizedRecordDate === normalizeDate(now);
        case 'previousDay': return normalizedRecordDate === normalizeDate(subDays(now, 1));
        case 'currentWeek': {
          const ws = normalizeDate(startOfWeek(now, { weekStartsOn: 1 }));
          const we = normalizeDate(endOfWeek(now, { weekStartsOn: 1 }));
          return normalizedRecordDate! >= ws! && normalizedRecordDate! <= we!;
        }
        case 'previousWeek': {
          const cws = startOfWeek(now, { weekStartsOn: 1 });
          const pws = normalizeDate(subWeeks(cws, 1));
          const pwe = normalizeDate(subDays(cws, 1));
          return normalizedRecordDate! >= pws! && normalizedRecordDate! <= pwe!;
        }
        case 'currentMonth': {
          const ms = normalizeDate(startOfMonth(now));
          const me = normalizeDate(endOfMonth(now));
          return normalizedRecordDate! >= ms! && normalizedRecordDate! <= me!;
        }
        case 'previousMonth': {
          const pms = normalizeDate(startOfMonth(subMonths(now, 1)));
          const pme = normalizeDate(endOfMonth(subMonths(now, 1)));
          return normalizedRecordDate! >= pms! && normalizedRecordDate! <= pme!;
        }
        case 'dateRange': {
          if (!startDate || !endDate) return false;
          const [sy, sm, sd] = startDate.split('-').map(Number);
          const [ey, em, ed] = endDate.split('-').map(Number);
          const rs = normalizeDate(new Date(sy, sm - 1, sd));
          const re = normalizeDate(new Date(ey, em - 1, ed));
          return normalizedRecordDate! >= rs! && normalizedRecordDate! <= re!;
        }
        default: return true;
      }
    });
  }, [rawData, dateFilter, startDate, endDate]);

  // Helper to get team for an agent
  const getAgentTeam = (agentName: string): string | null => {
    if (!agentName) return null;
    const normalized = agentName.toLowerCase().trim();
    let user = allUsers.find(u => u.sales_tracker_id && u.sales_tracker_id.toLowerCase().trim() === normalized);
    if (!user) user = allUsers.find(u => u.username && u.username.toLowerCase().trim() === normalized);
    return user?.team || null;
  };

  // Calculate metrics — scoped to Weekend Warriors only
  const filteredMetrics = useMemo(() => {
    const agentCounts: Record<string, number> = {};
    filteredData.forEach(record => {
      const agent = (record['Agent '] || record.Agent || 'Unknown').trim();
      if (agent && agent !== 'Unknown' && agent !== '') {
        agentCounts[agent] = (agentCounts[agent] || 0) + 1;
      }
    });

    const allAgents: AgentData[] = Object.entries(agentCounts)
      .map(([name, count]) => ({ name, count, team: getAgentTeam(name) }))
      .sort((a, b) => b.count - a.count);

    // Only Weekend Warriors
    const wwAgents = allAgents.filter(a => a.team === TEAM_NAME);
    const wwTotal = wwAgents.reduce((sum, a) => sum + a.count, 0);

    return {
      totalSales: wwTotal,
      activeAgents: wwAgents.length,
      wwAgents,
      wwTotal,
    };
  }, [filteredData, allUsers]);

  const { wwAgents } = filteredMetrics;

  // Pie chart data
  const agentPieData = useMemo((): PieChartData[] =>
    wwAgents.slice(0, 8).map((agent, index) => ({
      name: agent.name,
      value: agent.count,
      color: COLORS[index % COLORS.length],
    })), [wwAgents]);

  const formatNumber = (value: number): string => new Intl.NumberFormat('en-US').format(value);

  const getFilterLabel = (): string => {
    switch (dateFilter) {
      case 'today': return 'Today';
      case 'previousDay': return 'Previous Day';
      case 'currentWeek': return 'Current Week';
      case 'previousWeek': return 'Previous Week';
      case 'currentMonth': return 'Current Month';
      case 'previousMonth': return 'Previous Month';
      case 'dateRange':
        if (startDate && endDate) {
          const [sy, sm, sd] = startDate.split('-').map(Number);
          const [ey, em, ed] = endDate.split('-').map(Number);
          const start = format(new Date(sy, sm - 1, sd), 'MMM dd, yyyy');
          const end = format(new Date(ey, em - 1, ed), 'MMM dd, yyyy');
          return startDate === endDate ? start : `${start} - ${end}`;
        }
        return 'Date Range';
      case 'all': return 'All Time';
      default: return '';
    }
  };

  if (salesLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (salesError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Failed to load analytics data. Please check your connection and try again.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4">Partner Analytics</Typography>
          <Chip label="Weekend Warriors" sx={{ mt: 0.5, bgcolor: '#ff9800', color: 'white', fontWeight: 600 }} />
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select value={dateFilter} onChange={(e: SelectChangeEvent<string>) => setDateFilter(e.target.value)} displayEmpty>
              <MenuItem value="" disabled><em>Select Time Period</em></MenuItem>
              <MenuItem value="today">Current Day</MenuItem>
              <MenuItem value="previousDay">Previous Day</MenuItem>
              <MenuItem value="currentWeek">Current Week</MenuItem>
              <MenuItem value="previousWeek">Previous Week</MenuItem>
              <MenuItem value="currentMonth">Current Month</MenuItem>
              <MenuItem value="previousMonth">Previous Month</MenuItem>
              <MenuItem value="dateRange">Date Range</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          {dateFilter === 'dateRange' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2">From:</Typography>
              <TextField type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ minWidth: 150 }} InputLabelProps={{ shrink: true }} inputProps={{ max: endDate || undefined }} />
              <Typography variant="body2">To:</Typography>
              <TextField type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ minWidth: 150 }} InputLabelProps={{ shrink: true }} inputProps={{ min: startDate || undefined }} />
            </Box>
          )}
        </Box>
      </Box>

      {wwAgents.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No Weekend Warriors team data found for this period. Make sure users are assigned to the "Weekend Warriors" team in User Management.
        </Alert>
      )}

      {/* Daily Goal — Weekend Warriors */}
      {dateFilter === 'today' && (
        <GridLegacy container spacing={3} sx={{ mb: 3 }}>
          <GridLegacy size={{ xs: 12 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #e65100 0%, #f57c00 50%, #ff9800 100%)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box display="flex" alignItems="center">
                    <FlagIcon sx={{ mr: 2, fontSize: 50, color: 'white' }} />
                    <Box>
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                        Weekend Warriors Daily Goal: {DAILY_GOAL}
                      </Typography>
                      <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mt: 1 }}>
                        {filteredMetrics.wwTotal} / {DAILY_GOAL}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                        {filteredMetrics.wwTotal >= DAILY_GOAL
                          ? '🎉 Goal Achieved!'
                          : `${DAILY_GOAL - filteredMetrics.wwTotal} more to go!`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '40%' }, minWidth: 200 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((filteredMetrics.wwTotal / DAILY_GOAL) * 100, 100)}
                      sx={{
                        height: 20, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': { bgcolor: '#fff3e0', borderRadius: 2 }
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'white', mt: 1, textAlign: 'center' }}>
                      {Math.min(Math.round((filteredMetrics.wwTotal / DAILY_GOAL) * 100), 100)}% Complete
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </GridLegacy>
        </GridLegacy>
      )}

      {/* Summary Cards */}
      <GridLegacy container spacing={3} sx={{ mb: 4 }}>
        <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimelineIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Weekend Warriors Sales</Typography>
                  <Typography variant="h5">{formatNumber(filteredMetrics.totalSales)}</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>
        <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Active Partners</Typography>
                  <Typography variant="h5">{formatNumber(filteredMetrics.activeAgents)}</Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>
        <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>Avg Sales per Partner</Typography>
                  <Typography variant="h5">
                    {wwAgents.length > 0
                      ? formatNumber(Math.round(wwAgents.reduce((sum, a) => sum + a.count, 0) / wwAgents.length))
                      : '0'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">{getFilterLabel()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>
      </GridLegacy>

      <GridLegacy container spacing={3}>
        {/* Pie Chart */}
        <GridLegacy size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Weekend Warriors Distribution</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>Top 8 partners by sales count</Typography>
            {agentPieData.length > 0 ? (
              <Box sx={{ height: 400 }}>
                <Pie
                  data={{
                    labels: agentPieData.map(d => d.name),
                    datasets: [{
                      data: agentPieData.map(d => d.value),
                      backgroundColor: agentPieData.map(d => d.color),
                      borderColor: '#fff',
                      borderWidth: 2,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'right' as const, labels: { font: { size: 12 } } },
                      tooltip: { callbacks: { label: (ctx) => `${ctx.label || ''}: ${ctx.parsed || 0} sales` } }
                    },
                  }}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <Typography color="textSecondary">No partner data available for this period</Typography>
              </Box>
            )}
          </Paper>
        </GridLegacy>

        {/* Rankings */}
        <GridLegacy size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, maxHeight: 500, overflow: 'auto' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <TrophyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Weekend Warriors Leaderboard</Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Partners ranked by sales count for {getFilterLabel()}
            </Typography>
            <List dense>
              {wwAgents.length > 0 ? (
                wwAgents.map((agent, index) => (
                  <ListItem
                    key={index}
                    divider
                    sx={{ backgroundColor: index < 3 ? 'rgba(255, 152, 0, 0.08)' : 'transparent' }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{
                              bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#ff9800',
                              color: index < 3 ? '#000' : 'white',
                              fontWeight: 700,
                              minWidth: 45,
                            }}
                          />
                          <Typography variant="body2" fontWeight={index < 3 ? 700 : 400}>
                            {agent.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip
                            label={`${agent.count} sales`}
                            size="small"
                            sx={{ bgcolor: '#ff980020', color: '#e65100', fontWeight: 600 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary={<Typography color="textSecondary">No partner data available</Typography>} />
                </ListItem>
              )}
            </List>
          </Paper>
        </GridLegacy>
      </GridLegacy>
    </Container>
  );
};

export default PartnerAnalytics;
